import dayjs from 'dayjs';
import { memo, useMemo } from 'react';

import { Badge, Text } from '../../../shared/uiComponents';
import { TEXT } from '../../../shared/Helpers/colors';
import AuthorizationDetailedInfo from '../authorizationDetailedInfo';
import { findExpiredInfoColor } from '../helpers';
import { AdminAuthorizationCardProps, BCBAAuthorizationCardProps, TemporaryAuthorizationCardProps } from '../types';

import { AuthorizationDetailsProperties } from '../../../redux/API/ClientAPIHelpers/userClientProperties';

export const AuthorizationCardAdmin = ({ item }: AdminAuthorizationCardProps) => {
	const { authorization, client } = item;

	return (
		<div className="authorizationCard">
			<div className="info">
				<div className="row">
					<Text title="Client:" type="pxs" />
					<Text title={client.fullName} type="psm" fontWeight="medium" />
				</div>
				<div className="row">
					<Text title="Period:" type="pxs" />
					<Badge
						sizeType="sm"
						type={findExpiredInfoColor({ endDate: authorization.endDate })}
						label={`${dayjs(authorization.endDate).format('MM/DD/YYYY')}`}
					/>
				</div>
			</div>
		</div>
	);
};

export const TemporaryAuthorizationCard = ({ item }: TemporaryAuthorizationCardProps) => {
	const { temporaryAuthorization, client } = item;

	return (
		<div className="authorizationCard">
			<div className="info">
				<div className="row">
					<Text title="Client:" type="pxs" />
					<Text title={client.fullName} type="psm" fontWeight="medium" />
				</div>
				<div className="row">
					<Text title="Period:" type="pxs" />
					<Badge
						sizeType="sm"
						type={findExpiredInfoColor({
							endDate: temporaryAuthorization.endDate,
						})}
						label={`${dayjs(temporaryAuthorization.endDate).format('MM/DD/YYYY')}`}
					/>
				</div>
			</div>
		</div>
	);
};

export const AuthorizationCardBcba = memo(({ item }: BCBAAuthorizationCardProps) => {
	const authorizationBadges = useMemo(
		() =>
			item.authorizations.map((auth, index) => (
				<div key={`${auth.startDate}-${auth.endDate}-${index}`} className="badges">
					<Badge
						sizeType="sm"
						type={findExpiredInfoColor({ endDate: auth.endDate })}
						label={`${dayjs(auth.startDate).format('MM/DD/YYYY')} - ${dayjs(auth.endDate).format('MM/DD/YYYY')}`}
					/>
				</div>
			)),
		[item.authorizations],
	);

	return (
		<div className="authorizationCard">
			<div className="info">
				<div className="row">
					<Text title="Client:" type="pxs" />
					<Text title={item.client.fullName} type="psm" fontWeight="medium" />
				</div>
				<div className="row">
					<Text title="Period:" type="pxs" />
					{authorizationBadges}
				</div>
			</div>
			<div className="action">
				<AuthorizationDetailedInfo info={item} />
			</div>
		</div>
	);
});

export const AuthorizationDetailsCard = memo(({ item }: { item: AuthorizationDetailsProperties }) => {
	const dateRange = useMemo(
		() => `${dayjs(item.startDate).format('MM/DD/YYYY')} - ${dayjs(item.endDate).format('MM/DD/YYYY')}`,
		[item.startDate, item.endDate],
	);

	const authDetails = useMemo(
		() =>
			item.authorizationDetails.map((auth, index) => (
				<div key={`${auth.sessionType.name}-${auth.frequency.name}-${index}`} className="card">
					<div className="row">
						<Text title="CPT code:" type="pxs" />
						<div
							style={{
								padding: '4px 8px',
								borderRadius: '999px',
								width: 'max-content',
								height: 'max-content',
								color: TEXT.SECONDARY,
								fontFamily: 'Inter',
								fontSize: '12px',
								fontWeight: 500,
								lineHeight: '16px',
							}}
						>
							{auth.sessionType.name}
						</div>
					</div>
					<div className="row">
						<Text title="Frequency type:" type="pxs" />
						<Text title={auth.frequency.name} type="pxs" />
					</div>
					<div className="row">
						<Text title="Allocated:" type="pxs" />
						<Text title={auth.hoursAllocated} type="pxs" />
					</div>
					<div className="row">
						<Text title="Use:" type="pxs" />
						<Text title={auth.hoursUsed} type="pxs" />
					</div>
					<div className="row">
						<Text title="Remaining:" type="pxs" />
						<Text title={auth.hoursRemaining} type="pxs" />
					</div>
				</div>
			)),
		[item.authorizationDetails],
	);

	return (
		<div className="authorizationDetailsCard">
			<div className="date">
				<Text title={dateRange} type="psm" fontWeight="semiBold" />
			</div>
			<div className="content">{authDetails}</div>
		</div>
	);
});
AuthorizationDetailsCard.displayName = 'AuthorizationDetailsCard';
AuthorizationCardBcba.displayName = 'AuthorizationCardBcba';
